import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuthCore } from '../../core/modules/auth';
import { ERROR } from '../../core/constants/errorCode';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { isEmpty } from 'lodash';
import { WELCOME } from '../../core/router/routes';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { useGlobalCore } from '../../core/modules/global';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { useNavigationCore } from '../../core/router/navigation';
import ReactPlayer from 'react-player';
import TermsNotice from '../components/auth/TermsNotice';

interface FormData {
  email: string;
  password: string;
}

const schema = yup.object().shape({
  email: yup
    .string()
    .email('The format of the email is not correct')
    .required('You must enter an email'),
  password: yup.string().required('You must enter a password'),
});

const defaultValues: FormData = {
  email: '',
  password: '',
};

const SignUp = () => {
  const { t } = useTranslation();
  const { token, errorCode, signUp } = useAuthCore();
  const { closeAllDialog } = useGlobalCore();
  const { goToSignIn } = useNavigationCore();
  const [searchParams] = useSearchParams();

  const { control, formState, getValues, handleSubmit } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const [showPassword, setShowPassword] = useState(false);
  const [googleLoginError, setGoogleLoginError] = useState(false);
  const [showGoogleButton, setShowGoogleButton] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState('');

  useEffect(() => {
    const sId = searchParams.get("sId");

    if (!sId) {
      goToSignIn();
    } else {
      setSubscriptionId(sId);
    }
  }, [goToSignIn, searchParams]);

  const onSubmit = useCallback(() => {
    setShowGoogleButton(true);
  }, []);

  const errorMessage = useMemo(() => {
    if (errorCode) {
      if (errorCode === ERROR.ERR_401) {
        return 'Los datos ingresados son incorrectos o ya tiene un cuenta asociada a su cuenta de Google';
      } else {
        return 'Un error inexcperado a ocurrido, intente de nuevo en unos minutos';
      }
    }
  }, [errorCode]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const onGoogleLogin = useCallback((credentials: CredentialResponse) => {
    if (credentials.credential) {
      signUp({
        email: getValues().email,
        password:  getValues().password,
        token: credentials.credential,
        subscriptionId,
      }, closeAllDialog);
    }
  }, [closeAllDialog, getValues, signUp, subscriptionId]);

  if (token) {
    return <Navigate to={WELCOME} replace />;
  }

  return (
    <div className="flex flex-col sm:flex-row items-center md:items-start sm:justify-center md:justify-start flex-1 min-w-0">
      <Paper className="h-full sm:h-auto md:flex md:items-center md:justify-end w-full sm:w-auto md:h-full md:w-1/2 py-8 px-16 sm:p-48 md:p-64 sm:rounded-2xl md:rounded-none sm:shadow md:shadow-none ltr:border-r-1 rtl:border-l-1">
        <div className="w-full max-w-320 sm:w-320 mx-auto sm:mx-0">
          <img
            className="h-48"
            src={`${process.env.PUBLIC_URL}/assets/images/logo/vlogo.png`}
            alt="logo"
          />

          <div className="mt-32 text-4xl font-extrabold tracking-tight leading-tight">
            Completar cuenta
          </div>

          {!showGoogleButton ? (
            <form
              name="loginForm"
              noValidate
              className="flex flex-col justify-center w-full mt-32"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="mb-24">
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('label.email')}
                      autoFocus
                      type="email"
                      variant="outlined"
                      error={!!errors.email}
                      helperText={errors?.email?.message}
                      required
                      fullWidth
                    />
                  )}
                />
              </div>
              <div className="mb-24">
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('label.password')}
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      error={!!errors.password}
                      helperText={errors?.password?.message}
                      required
                      fullWidth
                      autoComplete=''
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityIcon />
                              ) : (
                                <VisibilityOffIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </div>

              <Button
                variant="contained"
                color="secondary"
                className="w-full"
                sx={{ borderRadius: 50 }}
                disabled={!isEmpty(errors) || !isValid}
                type="submit"
                size="large"
              >
                Continuar
              </Button>
            </form>
          ) : (
            <div className="flex flex-col justify-center w-full mt-32">
              <div className="flex justify-center md:justify-start">
                <GoogleLogin
                  onSuccess={onGoogleLogin}
                  size="large"
                  shape="square"
                  width="320"
                  locale="es"
                  onError={() => {
                    setGoogleLoginError(true);
                  }}
                />
              </div>

              <div className="text-md font-medium mt-8 text-right cursor-pointer" onClick={() => setShowGoogleButton(false)}>
                Volver
              </div>

              <TermsNotice />

              {errorMessage && (
                <div className="text-center mb-16">
                  <Typography variant="caption" className="text-red-500">
                    {errorMessage}
                  </Typography>
                </div>
              )}

              {googleLoginError && (
                <div className="text-center mt-16">
                  <Typography variant="caption" className="text-red-500">
                    Ocurrió un error en el login de Google
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </Paper>

      <Box
        className="relative hidden md:flex flex-auto items-center justify-center overflow-hidden"
        sx={{ backgroundColor: 'primary.main' }}
      >
        <div className="z-10 relative w-full">
          <ReactPlayer
            url={`${process.env.PUBLIC_URL}/assets/welcome.mp4`}
            width="100%"
            height="100%"
            volume={0}
            playing
            loop
          />
        </div>
      </Box>
    </div>
  );
};

export default SignUp;
